
import { supabase } from "@/integrations/supabase/client";

export interface AuthSecurityCheck {
  isRateLimited: boolean;
  isAccountLocked: boolean;
  canProceed: boolean;
  message?: string;
}

export const checkAuthSecurity = async (email: string, attemptType: 'signin' | 'signup' | 'reset'): Promise<AuthSecurityCheck> => {
  try {
    // Check rate limiting
    const { data: rateLimited, error: rateLimitError } = await supabase
      .rpc('is_rate_limited', { 
        email_address: email.toLowerCase().trim(), 
        attempt_type: attemptType 
      });

    if (rateLimitError) {
      console.error('Rate limit check error:', rateLimitError);
    }

    // Check account lockout for signin attempts
    let accountLocked = false;
    if (attemptType === 'signin') {
      const { data: locked, error: lockError } = await supabase
        .rpc('is_account_locked', { user_email: email.toLowerCase().trim() });
      
      if (lockError) {
        console.error('Account lock check error:', lockError);
      }
      accountLocked = locked || false;
    }

    const isRateLimited = rateLimited || false;
    const isAccountLocked = accountLocked;

    let message = '';
    if (isAccountLocked) {
      message = 'Account is temporarily locked due to multiple failed attempts. Please try again in 30 minutes.';
    } else if (isRateLimited) {
      message = 'Too many attempts. Please wait 15 minutes before trying again.';
    }

    return {
      isRateLimited,
      isAccountLocked,
      canProceed: !isRateLimited && !isAccountLocked,
      message
    };
  } catch (error) {
    console.error('Security check error:', error);
    return {
      isRateLimited: false,
      isAccountLocked: false,
      canProceed: true
    };
  }
};

export const logAuthAttempt = async (
  email: string,
  attemptType: 'signin' | 'signup' | 'reset',
  success: boolean,
  errorMessage?: string
) => {
  try {
    // Call edge function for server-side IP logging
    await supabase.functions.invoke('log-auth-attempt', {
      body: {
        email: email.toLowerCase().trim(),
        attemptType,
        success,
        errorMessage: errorMessage || null
      }
    });
  } catch (error) {
    console.error('Failed to log auth attempt:', error);
  }
};

export const updateSigninStats = async (userId: string) => {
  try {
    await supabase.rpc('update_signin_stats', { user_id: userId });
  } catch (error) {
    console.error('Failed to update signin stats:', error);
  }
};

export const handleFailedSignin = async (email: string) => {
  try {
    await supabase.rpc('handle_failed_signin', { user_email: email.toLowerCase().trim() });
  } catch (error) {
    console.error('Failed to handle failed signin:', error);
  }
};

// Password validation removed - using passwordless OTP authentication
